import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Aviso, Botao, Campo, Cartao, Chip } from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  linkDoFormulario,
  useCriarFormulario,
  useEquipesParaCanais,
  useFormularios,
  useGirarTokenDoFormulario,
  useOrganizacoesParaCanais,
  useRevogarFormulario,
} from "../../nucleo/canais";
import { CampoDeEscolha } from "../automacao/apoio";
import { SegredoMostradoUmaVez } from "../automacao/SegredoMostradoUmaVez";

export function PainelDeFormularios() {
  const formularios = useFormularios();
  const organizacoes = useOrganizacoesParaCanais();
  const equipes = useEquipesParaCanais();
  const criar = useCriarFormulario();
  const revogar = useRevogarFormulario();
  const girar = useGirarTokenDoFormulario();
  const [organizacaoId, setOrganizacaoId] = useState("");
  const [nome, setNome] = useState("");
  const [categoria, setCategoria] = useState("");
  const [equipeId, setEquipeId] = useState("");
  const [problema, setProblema] = useState<string | null>(null);
  const [link, setLink] = useState<{ titulo: string; valor: string } | null>(null);

  const nomes = useMemo(
    () => Object.fromEntries((organizacoes.data ?? []).map((o) => [o.id, o.nome_fantasia ?? o.razao_social])),
    [organizacoes.data],
  );

  function submeter(evento: FormEvent) {
    evento.preventDefault();
    const erro = !organizacaoId ? "Escolha a empresa." : nome.trim().length < 2 ? "Dê um nome ao formulário." : null;
    setProblema(erro);
    if (erro) return;
    criar.mutate(
      {
        organizacao_id: organizacaoId,
        nome: nome.trim(),
        categoria: categoria.trim() || null,
        equipe_id: equipeId || null,
      },
      {
        onSuccess: (criado) => {
          setLink({
            titulo: `Link do formulário ${criado.nome}`,
            valor: linkDoFormulario(window.location.origin, criado.token),
          });
          setNome("");
        },
      },
    );
  }

  const falha = criar.error ?? revogar.error ?? girar.error ?? formularios.error;

  return (
    <div>
      {link ? (
        <SegredoMostradoUmaVez
          titulo={link.titulo}
          valor={link.valor}
          rotuloDoBotao="Copiar link"
          aoFechar={() => setLink(null)}
          data-testid="link-do-formulario"
        />
      ) : null}
      <Cartao titulo="Novo formulário público" className="mb-4">
        <p className="nota-rodape mb-3">
          Quem abre o link pede atendimento sem entrar no portal. O chamado nasce na empresa escolhida, com o
          nome e o e-mail informados como contato (sem verificar). O link tem proteção contra robôs e limite
          de pedidos.
        </p>
        <form onSubmit={submeter} data-testid="formulario-de-formulario" noValidate>
          <CampoDeEscolha
            rotulo="Empresa do formulário"
            valor={organizacaoId}
            aoMudar={setOrganizacaoId}
            vazio="Escolha a empresa"
            opcoes={(organizacoes.data ?? []).map((o) => ({ valor: o.id, rotulo: o.nome_fantasia ?? o.razao_social }))}
          />
          <Campo rotulo="Nome do formulário" value={nome} onChange={(e) => setNome(e.target.value)} />
          <Campo rotulo="Categoria dos chamados (opcional)" value={categoria} onChange={(e) => setCategoria(e.target.value)} />
          <CampoDeEscolha
            rotulo="Equipe que recebe os chamados"
            valor={equipeId}
            aoMudar={setEquipeId}
            vazio="Nenhuma (fila geral)"
            opcoes={(equipes.data ?? []).map((e) => ({ valor: e.id, rotulo: e.nome }))}
          />
          {problema ? <Aviso tom="erro" className="mb-3">{problema}</Aviso> : null}
          {falha ? <Aviso tom="erro" className="mb-3">{mensagemDoConsole(falha)}</Aviso> : null}
          <Botao type="submit" disabled={criar.isPending}>{criar.isPending ? "Criando…" : "Criar formulário"}</Botao>
        </form>
      </Cartao>

      <section aria-labelledby="titulo-formularios">
        <h2 className="titulo-secao" id="titulo-formularios">Formulários públicos</h2>
        {formularios.data && formularios.data.length === 0 ? <Aviso tom="marca">Nenhum formulário criado.</Aviso> : null}
        <div className="lista-de-itens" data-testid="lista-de-formularios">
          {formularios.data?.map((item) => (
            <Cartao
              key={item.id}
              titulo={item.nome}
              data-testid={`formulario-${item.nome}`}
              acessorio={<Chip estado={item.ativo ? "ok" : "neutro"}>{item.ativo ? "Ativo" : "Desativado"}</Chip>}
            >
              <p>{nomes[item.organizacao_id] ?? "Empresa"} · link <code>{item.token_prefixo}…</code></p>
              <div className="flex gap-2 mt-2">
                {item.ativo ? (
                  <Botao type="button" variante="fantasma" aria-label={`Desativar o formulário ${item.nome}`} onClick={() => revogar.mutate(item.id)}>
                    Desativar o link
                  </Botao>
                ) : null}
                <Botao
                  type="button"
                  variante="fantasma"
                  aria-label={`Gerar um link novo para o formulário ${item.nome}`}
                  onClick={() =>
                    girar.mutate(item.id, {
                      onSuccess: (novo) =>
                        setLink({
                          titulo: `Novo link do formulário ${novo.nome}`,
                          valor: linkDoFormulario(window.location.origin, novo.token),
                        }),
                    })
                  }
                >
                  Gerar link novo
                </Botao>
              </div>
            </Cartao>
          ))}
        </div>
      </section>
    </div>
  );
}
